import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { isoOf } from "@/components/LeaveCalendar";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TYPE_CLS = { "Annual leave": "bg-sky-800/80", "Sick leave": "bg-rose-800/80", "Bereavement leave": "bg-violet-800/80", "Public holiday": "bg-amber-800/80" };
const short = n => { const [f, ...r] = (n || "").split(" "); return `${f} ${r.length ? r[r.length - 1][0] + "." : ""}`.trim(); };

function cells(y, m) {
  const first = (new Date(y, m, 1).getDay() + 6) % 7;
  return [...Array(first).fill(null), ...Array.from({ length: new Date(y, m + 1, 0).getDate() }, (_, i) => i + 1)];
}

// Admin/Manager month view of approved leave across the team, with NZ public holidays.
export default function TeamLeaveCalendar() {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [lines, setLines] = useState([]); const [hols, setHols] = useState({}); const [pick, setPick] = useState("");
  useEffect(() => {
    const start = isoOf(ym.y, ym.m, 1), end = isoOf(ym.y, ym.m, new Date(ym.y, ym.m + 1, 0).getDate());
    api.get("/leave/lines", { params: { start, end } }).then(r => setLines(r.data)).catch(() => setLines([]));
    api.get("/public-holidays").then(r => setHols(Object.fromEntries(r.data.map(h => [h.date, h.name])))).catch(() => {});
  }, [ym]);
  const shift = k => setYm(({ y, m }) => { const d = new Date(y, m + k, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const byDay = lines.reduce((a, l) => ((a[l.date] = a[l.date] || []).push(l), a), {});
  const label = new Date(ym.y, ym.m, 1).toLocaleDateString("en-NZ", { month: "long", year: "numeric" });
  const todayIso = isoOf(now.getFullYear(), now.getMonth(), now.getDate());
  const picked = byDay[pick] || [];
  return (
    <div className="card space-y-2" data-testid="team-leave-calendar">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-silver whitespace-nowrap">Team leave calendar</h2>
        <div className="flex items-center gap-1 flex-wrap">
          <button type="button" className="btn-ghost px-3 py-1.5" onClick={() => shift(-1)} aria-label="Previous month" data-testid="tlc-prev"><ChevronLeft size={16} /></button>
          <span className="min-w-[9rem] text-center" data-testid="tlc-month">{label}</span>
          <button type="button" className="btn-ghost px-3 py-1.5" onClick={() => shift(1)} aria-label="Next month" data-testid="tlc-next"><ChevronRight size={16} /></button>
          <button type="button" className="btn-ghost text-xs px-3 py-1.5" onClick={() => setYm({ y: now.getFullYear(), m: now.getMonth() })} data-testid="tlc-today">Today</button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] opacity-60">{DOW.map(d => <div key={d}>{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells(ym.y, ym.m).map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const iso = isoOf(ym.y, ym.m, d); const day = byDay[iso] || []; const hol = hols[iso];
          return (
            <button type="button" key={iso} onClick={() => setPick(iso)} data-testid={`tlc-day-${iso}`}
              className={`min-h-[4.5rem] rounded border text-left p-1 transition-colors ${pick === iso ? "border-silver" : "border-white/10"} ${i % 7 >= 5 ? "bg-white/[0.02]" : ""} ${hol ? "ring-1 ring-amber-400/60" : ""}`}>
              <div className={`text-xs ${iso === todayIso ? "font-bold text-silver" : "opacity-70"}`}>{d}</div>
              {hol && <div className="text-[10px] text-amber-300 truncate" title={hol}>{hol}</div>}
              {day.slice(0, 3).map((l, k) => <div key={k} className={`text-[10px] rounded px-1 mt-0.5 truncate ${TYPE_CLS[l.type] || "bg-neutral-700"}`} title={`${l.worker_name} — ${l.type}`}>{short(l.worker_name)}</div>)}
              {day.length > 3 && <div className="text-[10px] opacity-70">+{day.length - 3} more</div>}
            </button>
          );
        })}
      </div>
      <div className="flex gap-3 flex-wrap text-[11px] opacity-80">
        {Object.entries(TYPE_CLS).map(([t, c]) => <span key={t}><span className={`inline-block w-2.5 h-2.5 rounded-sm mr-1 ${c}`} />{t}</span>)}
        <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 ring-1 ring-amber-400" />NZ public holiday</span>
      </div>
      {pick && (
        <div className="text-sm border-t border-white/10 pt-2" data-testid="tlc-picked">
          <b>{new Date(pick + "T00:00:00").toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" })}</b>
          {hols[pick] && <span className="text-amber-300"> · {hols[pick]}</span>}
          {picked.length === 0 ? <span className="opacity-60"> · Nobody on approved leave</span>
            : <ul className="mt-1">{picked.map((l, k) => <li key={k} data-testid={`tlc-person-${k}`}>{l.worker_name} — {l.type} ({l.hours} h)</li>)}</ul>}
        </div>
      )}
      <div className="text-[11px] opacity-50">Approved leave only, active staff. Pending requests are listed below.</div>
    </div>
  );
}
